// Page sélection des métiers — /diagnostic/nouveau
// Première étape du tunnel : choisir 1 à 3 métiers avant de lancer le diagnostic

import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Info } from 'lucide-react'
import { DiagnosticLayout, useDiagnosticSteps } from '../components/layout'
import MetierCard from '../components/ui/MetierCard'
import { Badge } from '../components/ui/badge'
import { api } from '../services/api'
import { trackEvent } from '../lib/analytics'
import { useEntreprise } from '../contexts/EntrepriseContext'

interface MetierWithDescription {
  code: string
  nom: string
  description?: string
}

const MAX_METIERS = 3

// Métiers par défaut (fallback si API indisponible)
const DEFAULT_METIERS: MetierWithDescription[] = [
  { code: 'CAN', nom: 'Pose de canalisation' },
  { code: 'CAR', nom: 'Pose de carrelage-mosaïque' },
  { code: 'CHB', nom: 'Charpente et structure bois' },
  { code: 'CHM', nom: 'Charpente métallique' },
  { code: 'CRO', nom: 'Travaux routiers - Signalisation temporaire' },
  { code: 'COU', nom: 'Couverture - Zinguerie' },
  { code: 'DEM', nom: 'Curage- démolition-déconstruction' },
  { code: 'DES', nom: 'Désamiantage' },
  { code: 'ECH', nom: 'Montage et démontage d\'échafaudage' },
  { code: 'ELE', nom: 'Electricien' },
  { code: 'END', nom: 'Façade-Enduit' },
  { code: 'ETA', nom: 'Etanchéité - Bardage' },
  { code: 'MAC', nom: 'Maçon' },
  { code: 'MEN', nom: 'Menuiserie PVC, bois et agencement' },
  { code: 'PAY', nom: 'Aménagements urbains et paysagers' },
  { code: 'PEI', nom: 'Peinture - Miroiterie - Vitrerie - Pose de revêtement' },
  { code: 'PLA', nom: 'Plâtrerie-Plaquisterie-Plafonds' },
  { code: 'PLO', nom: 'Plomberie-Génie climatique' },
  { code: 'ISO', nom: 'Isolation' },
  { code: 'SER', nom: 'Serrurerie-Métallerie-Menuiserie Métallique' },
  { code: 'TAI', nom: 'Taille de Pierre- Marbrerie-Monuments historiques' },
  { code: 'TER', nom: 'Terrassier' },
]

export default function SelectionMetiersPage() {
  const navigate = useNavigate()
  const { currentEntreprise, currentCompany, currentSiret } = useEntreprise()
  const steps = useDiagnosticSteps('metiers')

  const [metiers, setMetiers] = useState<MetierWithDescription[]>([])
  const [selectedMetiers, setSelectedMetiers] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Charger les métiers depuis l'API
  useEffect(() => {
    async function loadMetiers() {
      try {
        const data = await api.getMetiers()
        setMetiers(data.map((m: any) => ({
          code: m.code,
          nom: m.nom,
          description: m.description && m.description !== m.nom
            ? m.description
            : m.nomComplet && m.nomComplet !== m.nom
              ? m.nomComplet
              : undefined,
        })))
      } catch (error) {
        console.error('Erreur chargement metiers:', error)
        setMetiers(DEFAULT_METIERS)
      }
    }
    loadMetiers()
  }, [])

  // Toggle sélection avec garde max
  const handleSelect = useCallback((code: string) => {
    setSelectedMetiers(prev => {
      if (prev.includes(code)) return prev.filter(c => c !== code)
      if (prev.length >= MAX_METIERS) return prev
      return [...prev, code]
    })
  }, [])

  // Créer le diagnostic et naviguer vers Q1
  const handleSubmit = async () => {
    if (selectedMetiers.length === 0 || isSubmitting) return
    trackEvent('commencer le diagnostic')
    setIsSubmitting(true)

    try {
      const metiersSelectionnes = selectedMetiers.map((code, index) => ({
        code,
        nom: metiers.find(m => m.code === code)?.nom || code,
        ordre: index + 1,
      }))

      const raisonSociale = currentEntreprise || 'Mon entreprise'

      const result = await api.createDiagnostic({
        entreprise: {
          raisonSociale,
          siret: currentSiret || undefined,
          effectif: currentCompany?.employeeCount,
        },
        metierIds: selectedMetiers,
      })

      navigate(`/diagnostic/${result.id}/situations`, {
        state: {
          metierIds: selectedMetiers,
          metiersSelectionnes,
          currentMetierIndex: 0,
        },
      })
    } catch (error) {
      console.error('Erreur creation diagnostic:', error)
      alert('Une erreur est survenue. Veuillez réessayer.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const maxReached = selectedMetiers.length >= MAX_METIERS

  return (
    <DiagnosticLayout
      steps={steps}
      onBack={() => navigate('/dashboard')}
      onNext={handleSubmit}
      backLabel="Retour"
      nextLabel={isSubmitting ? 'Création...' : 'Commencer le diagnostic'}
      nextDisabled={selectedMetiers.length === 0 || isSubmitting}
    >
      <div>
        {/* Titre */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Quels sont vos métiers ?</h1>
          <p className="text-muted-foreground mt-2">
            Sélectionnez jusqu'à 3 métiers correspondant à l'activité de votre entreprise
          </p>
        </div>

        {/* Indication + compteur */}
        <div className="flex items-center justify-between mb-6">
          {selectedMetiers.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Info className="size-4" />
              <span>1 à 3 métiers maximum</span>
            </div>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant={maxReached ? 'default' : 'secondary'}>
                {selectedMetiers.length}/{MAX_METIERS}
              </Badge>
              {selectedMetiers.map((code) => {
                const metier = metiers.find(m => m.code === code)
                return (
                  <Badge key={code} variant="outline" className="gap-1.5">
                    {metier?.nom}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleSelect(code) }}
                      className="ml-1 hover:text-destructive"
                      aria-label={`Retirer ${metier?.nom || code}`}
                    >
                      &#x2715;
                    </button>
                  </Badge>
                )
              })}
            </div>
          )}
        </div>

        {/* Grille alignée */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {metiers.map((metier) => (
            <MetierCard
              key={metier.code}
              code={metier.code}
              nom={metier.nom}
              description={metier.description}
              selected={selectedMetiers.includes(metier.code)}
              selectionOrder={
                selectedMetiers.includes(metier.code)
                  ? selectedMetiers.indexOf(metier.code) + 1
                  : undefined
              }
              maxReached={maxReached}
              onSelect={handleSelect}
            />
          ))}
        </div>
      </div>
    </DiagnosticLayout>
  )
}
